import { useRouter } from 'next/router';
import { useReduxState } from 'hooks';
import { breakpoints } from 'styles';
import { useApiData } from 'lib/api';
import { InfoCountTile } from 'components/common/info';

import type { GetHomePageUserCountsRes } from 'shared-types/responses';
import type { HomeDetailsViewType } from './HomeDetailsView';

const HomeHeaderUserCounts = () => {
  const {
    messages,
    serverConfig: {
      hideAchievementsFromUI,
      hideCalendarEventsFromUI,
      hideCoursesFromUI,
      hidePathsFromUI,
      homePageShowExpiringCompletions,
      homePageShowUpcomingDeadlines,
      homePageShowUpcomingEvents
    },
    user
  } = useReduxState();

  const router = useRouter();

  const { data } = useApiData<GetHomePageUserCountsRes>({
    url:
      `/admin/home-page/user-counts?` +
      `completions=${
        homePageShowExpiringCompletions &&
        (!hideAchievementsFromUI || !hideCoursesFromUI)
      }` +
      `&deadlines=${
        homePageShowUpcomingDeadlines &&
        (!hideCoursesFromUI || !hidePathsFromUI)
      }` +
      `&events=${homePageShowUpcomingEvents && !hideCalendarEventsFromUI}`,
    onlyIf:
      Boolean(user?.id) &&
      (homePageShowExpiringCompletions ||
        homePageShowUpcomingDeadlines ||
        homePageShowUpcomingEvents)
  });

  const handleNavigation = (view: HomeDetailsViewType) => {
    router.push({ query: { ...router.query, view } }, undefined, {
      shallow: true
    });
  };

  const showCounts =
    (homePageShowExpiringCompletions && data?.expiringCompletions) ||
    (homePageShowUpcomingDeadlines && data?.upcomingDeadlines) ||
    (homePageShowUpcomingEvents && data?.upcomingEvents);

  return !showCounts ? null : (
    <>
      {homePageShowExpiringCompletions && data?.expiringCompletions ? (
        <div className="count">
          <InfoCountTile
            category="warning"
            icon="hourglass-start"
            onClick={() => handleNavigation('expiring-completions')}
            text={messages.completionsExpiring}
            value={data?.expiringCompletions}
          />
        </div>
      ) : null}

      {homePageShowUpcomingDeadlines && data?.upcomingDeadlines ? (
        <div className="count">
          <InfoCountTile
            category="warning"
            icon={['far', 'clock']}
            onClick={() => handleNavigation('upcoming-target-schedules')}
            text={messages.commonUpcomingTargetSchedules}
            value={data?.upcomingDeadlines}
          />
        </div>
      ) : null}

      {homePageShowUpcomingEvents && data?.upcomingEvents ? (
        <div className="count">
          <InfoCountTile
            category="default"
            icon={['far', 'calendar-days']}
            onClick={() => handleNavigation('upcoming-events')}
            text={messages.eventCalendarUpcomingEvents}
            value={data?.upcomingEvents}
          />
        </div>
      ) : null}

      <style jsx>{`
        .count {
          width: 100%;
        }
        @media (min-width: ${breakpoints.small}px) {
          .count {
            width: calc(50% - 15px);
          }
        }
        @media (min-width: ${breakpoints.medium}px) {
          .count {
            width: calc(33.333% - 20px);
          }
        }
        @media (min-width: ${breakpoints.large}px) {
          .count {
            width: calc(25% - 22.5px);
          }
        }
      `}</style>
    </>
  );
};

export default HomeHeaderUserCounts;
